import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Select, ServiceChips } from "@/components/shared";
import { DocumentManager } from "@/components/DocumentManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CONTRACTOR_SERVICES, ONBOARDING_FORM_URL, SUBCONTRACTOR_AGREEMENT_URL, onboardingStatus } from "@/lib/constants";
import { Power, PowerOff, MapPin, Navigation, Info, Clock, Briefcase, Wrench, Trash2, AlertTriangle, ClipboardCheck, ExternalLink, CheckCircle2, PlayCircle, DollarSign } from "lucide-react";
import { TutorialModal } from "@/components/TutorialModal";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";

export default function ContractorHub() {
  const { user, refresh, logout } = useAuth();
  const [available, setAvailable] = useState(!!user?.available);
  const [zips, setZips] = useState((user?.service_zips || []).join(", "));
  const [counties, setCounties] = useState((user?.service_counties || []).join(", "));
  const [radius, setRadius] = useState(user?.service_radius || 60);
  const [residenceZip, setResidenceZip] = useState(user?.residence_zip || "");
  const [services, setServices] = useState(user?.services || []);
  const [showTut, setShowTut] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadJobs = useCallback(() => {
    setLoadingJobs(true);
    setJobsError(false);
    api.get("/contractor/available-jobs").then((r) => setJobs(r.data)).catch(() => setJobsError(true)).finally(() => setLoadingJobs(false));
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { available, service_zips: toArr(zips), service_counties: toArr(counties), service_radius: radius, residence_zip: residenceZip, services });
      toast.success("Profile saved");
      refresh?.();
      loadJobs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async () => {
    const next = !available;
    setAvailable(next);
    try { await api.put("/profile", { available: next }); toast.success(next ? "You're now AVAILABLE for work" : "Marked unavailable"); refresh?.(); }
    catch (err) { setAvailable(!next); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const acceptJob = async (jid) => {
    try { await api.post(`/jobs/${jid}/accept`); toast.success("Job accepted — it's now assigned to you"); loadJobs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [amounts, setAmounts] = useState({});
  const bidJob = async (jid) => {
    const amt = parseFloat(amounts[jid]);
    if (!amt || amt <= 0) { toast.error("Enter your bid amount"); return; }
    try { await api.post(`/jobs/${jid}/payout-bids`, { amount: amt, note: "Bid from My Work" }); toast.success("Bid sent to dispatch — you'll be notified when it's reviewed"); setAmounts((a) => ({ ...a, [jid]: "" })); loadJobs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const claimJob = async (jid) => {
    try { await api.post(`/jobs/${jid}/interest`); toast.success("Dispatch notified — you're on the list to claim this job"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const shareLocation = (jobId) => {
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    toast.loading("Getting your location…", { id: "loc" });
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/jobs/${jobId}/share-location`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success(data.distance_mi != null ? `~${data.distance_mi} mi away · ETA ~${data.eta_min} min (sent to admin)` : "Location shared with admin", { id: "loc" });
        loadJobs();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail), { id: "loc" }); }
    }, () => toast.error("Location permission denied", { id: "loc" }), { enableHighAccuracy: true, timeout: 10000 });
  };

  const deleteProfile = async () => {
    try { await api.delete("/profile"); toast.success("Your profile was deleted"); logout?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const formDone = !!user?.onboarding_completed;
  const agrDone = !!user?.agreement_signed;
  const ob = onboardingStatus(user?.created_at, formDone && agrDone);
  const markStep = async (field) => {
    try { await api.put("/profile", { [field]: true }); toast.success(field === "agreement_signed" ? "Agreement marked signed" : "Form marked complete"); refresh?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Contractor</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">My Profile</h1>
        <Btn variant="secondary" data-testid="open-tutorial-btn" onClick={() => setShowTut(true)} className="mt-3"><PlayCircle size={16} /> Watch Tutorial & Download Guide</Btn>
      </div>

      {/* Availability */}
      <Card className="p-5 flex flex-wrap items-center justify-between gap-4" data-testid="availability-card">
        <div>
          <Label>Work Status</Label>
          <div className="font-mono text-xs text-zinc-500">{available ? "Dispatch can match you to nearby jobs." : "You won't be matched to jobs while unavailable."}</div>
        </div>
        <Btn data-testid="availability-toggle" variant={available ? "primary" : "secondary"} onClick={toggle}>
          {available ? <><Power size={16} /> Available</> : <><PowerOff size={16} /> Unavailable</>}
        </Btn>
      </Card>

      <TutorialModal open={showTut} onClose={() => setShowTut(false)} role="crew" />

      {user?.must_change_password && <ChangePasswordCard />}

      {!ob.done && (
        <Card className={`p-4 space-y-3 ${ob.overdue ? "!border-red-600 bg-red-950/20" : "!border-[#FF5722] bg-[#FF5722]/10"}`} data-testid="onboarding-card">
          <div className="flex items-start gap-2">
            <ClipboardCheck size={18} className={`mt-0.5 shrink-0 ${ob.overdue ? "text-red-400" : "text-[#FF5722]"}`} />
            <div className="text-sm text-white">
              <div className="font-bold uppercase font-head">{ob.overdue ? "Onboarding overdue" : "Complete your onboarding"}</div>
              <div className="text-zinc-300">Finish BOTH steps below{ob.overdue ? ` — due ${ob.daysOver} day(s) ago.` : ob.daysLeft != null ? ` within ${ob.daysLeft} day(s) of joining.` : "."}</div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap border-t border-white/10 pt-3">
            <span className="text-sm text-white flex items-center gap-1.5">{formDone ? <CheckCircle2 size={15} className="text-emerald-400" /> : <span className="inline-block w-[15px]" />} 1. Onboarding form{formDone ? " — done" : ""}</span>
            <div className="flex gap-2">
              <a href={ONBOARDING_FORM_URL} target="_blank" rel="noreferrer" data-testid="onboarding-form-link"><Btn variant="secondary"><ExternalLink size={14} /> Open</Btn></a>
              {!formDone && <Btn data-testid="mark-form-btn" onClick={() => markStep("onboarding_completed")}>I've done this</Btn>}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap border-t border-white/10 pt-3">
            <span className="text-sm text-white flex items-center gap-1.5">{agrDone ? <CheckCircle2 size={15} className="text-emerald-400" /> : <span className="inline-block w-[15px]" />} 2. Subcontractor agreement{agrDone ? " — signed" : ""}</span>
            <div className="flex gap-2">
              <a href={SUBCONTRACTOR_AGREEMENT_URL} target="_blank" rel="noreferrer" data-testid="onboarding-agreement-link"><Btn variant="secondary"><ExternalLink size={14} /> Open</Btn></a>
              {!agrDone && <Btn data-testid="mark-agreement-btn" onClick={() => markStep("agreement_signed")}>I've signed it</Btn>}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4" data-testid="documents-card">
        <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5 mb-2"><ClipboardCheck size={15} className="text-[#FF5722]" /> Onboarding Documents</div>
        <p className="text-zinc-500 text-xs mb-3">Complete both as part of your onboarding. They stay available on your profile anytime.</p>
        <div className="flex flex-wrap gap-2">
          <a href={ONBOARDING_FORM_URL} target="_blank" rel="noreferrer" data-testid="doc-onboarding-form"><Btn variant="secondary"><ExternalLink size={14} /> Onboarding Form</Btn></a>
          <a href={SUBCONTRACTOR_AGREEMENT_URL} target="_blank" rel="noreferrer" data-testid="doc-agreement"><Btn variant="secondary"><ExternalLink size={14} /> Subcontractor Agreement</Btn></a>
        </div>
      </Card>

      {!available && (
        <Card className="p-4 !border-[#FF5722] bg-[#FF5722]/10 flex items-start gap-2" data-testid="availability-reminder">
          <AlertTriangle size={18} className="text-[#FF5722] mt-0.5 shrink-0" />
          <div className="text-sm text-white">You're currently <span className="font-bold">UNAVAILABLE</span>. Toggle <span className="text-[#FF5722] font-bold">AVAILABLE</span> above so dispatch can match you with work orders in your area.</div>
        </Card>
      )}

      {/* Services offered */}
      <Card className="p-5 space-y-3" data-testid="services-card">
        <div>
          <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><Wrench size={15} className="text-[#FF5722]" /> Services You Offer</div>
          <p className="text-zinc-500 text-xs mt-1">Select every service you can perform. Work orders are matched to your services and your distance.</p>
        </div>
        <ServiceChips selected={services} onChange={setServices} options={CONTRACTOR_SERVICES} />
      </Card>

      {/* Service areas */}
      <Card className="p-5 space-y-4" data-testid="service-area-card">
        <div>
          <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><MapPin size={15} className="text-[#FF5722]" /> Service Areas</div>
          <p className="text-zinc-500 text-xs mt-1">Enter the ZIP codes you cover (used to match you to work orders) and the counties you serve.</p>
        </div>
        <div><Label>Residence ZIP code</Label><Input data-testid="residence-zip" value={residenceZip} onChange={(e) => setResidenceZip(e.target.value)} placeholder="33801" /></div>
        <div><Label>Service ZIP codes (comma-separated)</Label><Input data-testid="service-zips" value={zips} onChange={(e) => setZips(e.target.value)} placeholder="30301, 30324, 30060" /></div>
        <div><Label>Counties served (comma-separated)</Label><Input data-testid="service-counties" value={counties} onChange={(e) => setCounties(e.target.value)} placeholder="Fulton, DeKalb, Cobb" /></div>
        <div>
          <Label>Coverage radius — how far you'll travel for work</Label>
          <Select data-testid="service-radius" value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
            {[20, 30, 40, 50, 60, 70, 80, 90, 100].map((r) => <option key={r} value={r}>{r} miles</option>)}
          </Select>
          <p className="text-zinc-500 text-xs mt-1">You'll only be matched to and alerted about jobs within this distance of your service ZIP codes.</p>
        </div>
        <Btn data-testid="save-profile-btn" onClick={saveProfile} disabled={saving}>{saving ? "Saving…" : "Save & Find Jobs"}</Btn>
      </Card>

      {/* Nearby jobs */}
      <div>
        <div className="font-head font-bold uppercase text-white text-sm tracking-wide mb-2 flex items-center gap-1.5"><Briefcase size={15} className="text-[#FF5722]" /> Available Work Nearby (within {radius} mi)</div>
        {loadingJobs ? (
          <Card className="p-6 text-center text-zinc-600 font-mono">Finding jobs…</Card>
        ) : jobsError ? (
          <Card className="p-6 text-center font-mono" data-testid="jobs-error">
            <p className="text-red-400 text-sm">Couldn't load nearby work right now.</p>
            <Btn variant="secondary" className="mt-3" data-testid="retry-jobs-btn" onClick={loadJobs}>Try again</Btn>
          </Card>
        ) : jobs.length === 0 ? (
          <Card className="p-6 text-center text-zinc-600 font-mono" data-testid="no-jobs">{toArr(zips).length === 0 && !residenceZip ? "Add your service ZIP codes above to see nearby work orders." : !available ? "Toggle AVAILABLE above to receive work orders." : "No active work orders in your service area right now — check back soon."}</Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {jobs.map((j) => (
              <Card key={j.id} data-testid={`avail-job-${j.id}`} className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">{j.service_type}</span>
                  <span className="font-mono text-xs text-white bg-zinc-800 border border-black px-1.5 py-0.5">{j.distance_mi} mi</span>
                </div>
                <div className="flex items-start gap-1.5 mt-1 text-white font-head font-bold"><MapPin size={14} className="mt-0.5 text-zinc-500 shrink-0" />{j.address}</div>
                {j.assigned_to_me && <span className="inline-block mt-1 font-mono text-[9px] uppercase bg-[#FF5722] text-black border border-black px-1.5 py-0.5">Assigned to you</span>}
                {j.scope && <p className="text-zinc-400 text-xs mt-1.5 line-clamp-2">{j.scope}</p>}
                {j.allowable_payout != null && !j.assigned && <div className="mt-2 font-mono text-xs text-zinc-300" data-testid={`payout-hint-${j.id}`}>Pays up to <span className="text-[#FF5722] font-bold">${Number(j.allowable_payout).toFixed(2)}</span>{j.bid_count > 0 ? ` · ${j.bid_count} bid${j.bid_count > 1 ? "s" : ""} pending` : ""}</div>}
                {j.my_bid && <div className={`mt-1 font-mono text-[11px] ${j.my_bid.status === "approved" ? "text-emerald-400" : j.my_bid.status === "declined" ? "text-red-400" : "text-amber-400"}`} data-testid={`my-bid-${j.id}`}>Your bid: ${Number(j.my_bid.amount).toFixed(2)} · {j.my_bid.status}</div>}
                {j.assigned && !j.assigned_to_me && <p className="text-amber-400 text-xs mt-2" data-testid={`taken-msg-${j.id}`}>This order has been taken by someone else — but you can tell dispatch you're available to claim it.</p>}
                {!j.assigned && (!j.my_bid || j.my_bid.status !== "pending") && (
                  <div className="flex items-center gap-2 mt-3">
                    <Input data-testid={`bid-amount-${j.id}`} type="number" step="0.01" value={amounts[j.id] || ""} onChange={(e) => setAmounts((a) => ({ ...a, [j.id]: e.target.value }))} placeholder="Your price $" className="max-w-[140px]" />
                    <Btn variant="secondary" data-testid={`bid-job-${j.id}`} onClick={() => bidJob(j.id)}><DollarSign size={13} /> Bid</Btn>
                  </div>
                )}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {!j.assigned && <Btn data-testid={`accept-job-${j.id}`} onClick={() => acceptJob(j.id)}>Accept Job</Btn>}
                  {j.assigned && !j.assigned_to_me && <Btn data-testid={`claim-job-${j.id}`} onClick={() => claimJob(j.id)}>I'm available — claim it</Btn>}
                  <Btn variant="secondary" data-testid={`share-loc-${j.id}`} onClick={() => shareLocation(j.id)}><Navigation size={13} /> Share Location / ETA</Btn>
                  <Link to={`/jobs/${j.id}`}><Btn variant="secondary">Open</Btn></Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* My documents */}
      <Card className="p-5" data-testid="my-documents-card">
        <DocumentManager basePath="/profile/documents" title="My Documents" hint="Upload your insurance, price list, W-9 or a larger counter-bid document. Dispatch can also add documents here for you." testid="my-doc-manager" />
      </Card>

      {!user?.must_change_password && <ChangePasswordCard />}

      {/* Instructions */}
      <Card className="p-5" data-testid="contractor-instructions">
        <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><Info size={15} className="text-[#FF5722]" /> How it works for contractors</div>
        <ol className="mt-3 space-y-2 text-sm text-zinc-300 list-decimal list-inside">
          <li><span className="text-white font-semibold">Install the app</span> on your phone: open this site in your browser and tap "Add to Home Screen".</li>
          <li><span className="text-white font-semibold">Pick the services you offer</span> and <span className="text-white font-semibold">set your service ZIP codes, counties & radius</span> so dispatch can match you to the right work orders.</li>
          <li><span className="text-[#FF5722] font-semibold">Remember to toggle "Available"</span> whenever you can take work — you will NOT be matched or alerted while unavailable.</li>
          <li><span className="text-white font-semibold">Share your location</span> on a job to send the admin your live distance and ETA.</li>
          <li className="flex items-start gap-1"><Clock size={14} className="mt-0.5 text-[#FF5722] shrink-0" /><span><span className="text-white font-semibold">Check in</span> when you arrive and <span className="text-white font-semibold">check out</span> when you finish — open the job and use the On-Site Time Tracking buttons.</span></li>
        </ol>
      </Card>

      {/* Danger zone */}
      <Card className="p-5 !border-red-900" data-testid="danger-zone">
        <div className="font-head font-bold uppercase text-red-400 text-sm tracking-wide flex items-center gap-1.5"><Trash2 size={15} /> Delete Profile</div>
        <p className="text-zinc-500 text-xs mt-1">Permanently remove your contractor account and unassign you from jobs. This cannot be undone.</p>
        <Btn variant="danger" className="mt-3 text-xs px-3 py-1.5" data-testid="delete-profile-btn" onClick={() => setConfirmDelete(true)}><Trash2 size={12} /> Delete My Profile</Btn>
      </Card>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-[#141417] border-2 border-red-600 text-white rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase text-red-400">Are you sure?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-300">This permanently deletes your contractor profile and logs you out. This action cannot be undone.</p>
          <div className="flex gap-2 justify-end mt-2">
            <Btn variant="secondary" onClick={() => setConfirmDelete(false)}>Cancel</Btn>
            <Btn variant="danger" data-testid="confirm-delete-profile" onClick={deleteProfile}>Yes, delete my profile</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
